import Button from '@/shared/components/Button';
import Checkbox from '@/shared/components/Checkbox';
import {Box, Skeleton, Stack, Typography} from '@mui/material';
import {useState, useEffect} from 'react';
import {CustomizationStyle} from './Customization.style';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import {toast} from 'react-toastify';
import {getErrorMessage, getImagePreview} from '@/utils/helpers';
import {PrintableService} from '@/utils/services/organization-printables';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {sendPresignedImage} from '../../Profile/General/ChangePhotoModal';

const Customization = () => {
	const currentEvent = useAppSelector(getCurrentEventSelector);

	const [withImage, setWithImage] = useState(false);
	const [Logo, setLogo] = useState<File | string | null>(null);

	const handleImageChange = (logo: File | null) => {
		setLogo(logo);
	};

	const getPrintableTemplateLogo = async () => {
		if (!currentEvent?.id) return;
		try {
			const res = await PrintableService.getPrinttableTemplate(currentEvent.id);
			const logoPath = res?.data.logoUrl;

			setLogo(logoPath);
			setWithImage(!!logoPath);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleLogoSave = async () => {
		if (!currentEvent?.id) return;

		try {
			let url: string | null = null;

			if (withImage) {
				if (Logo instanceof File) {
					const presignedImgUrl = await PrintableService.presignPrinttableTemplate();
					if (presignedImgUrl) {
						await sendPresignedImage(presignedImgUrl.data.url, Logo);
						url = presignedImgUrl.data.key.split('/').pop() || null;
					}
				} else if (typeof Logo === 'string') {
					url = Logo.split('/')[4]?.split('?')[0] || null;
				}
			} else {
				url = null;
			}

			const reqBody = {eventId: currentEvent.id, logoUrl: url};
			await PrintableService.createOrUpdatePrinttableTemplate(reqBody);

			await getPrintableTemplateLogo();
			toast.success('Settings saved successfully');
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleCancelClick = () => {
		getPrintableTemplateLogo();
	};

	useEffect(() => {
		if (!withImage) {
			setLogo(null);
		}
	}, [withImage]);

	useEffect(() => {
		getPrintableTemplateLogo();
	}, [currentEvent?.id]);

	return (
		<Box sx={CustomizationStyle.mainWrapper}>
			<Box sx={CustomizationStyle.leftBoxWrapper}>
				<Box>
					<Typography sx={CustomizationStyle.heading}>Printables Settings</Typography>
					<Box sx={CustomizationStyle.checkBoxContainer}>
						<Checkbox
							label="Add logo to bid sheets"
							value={withImage}
							onChange={(event, checked) => setWithImage(checked)}
						/>
						<Typography sx={CustomizationStyle.logoText}>Logo</Typography>
						<Box sx={CustomizationStyle.logoWrapper}>
							<GreyUploadImage
								onChange={handleImageChange}
								image={Logo}
								parentClassName={CustomizationStyle.logo}
								disabled={!withImage}
							/>
						</Box>
					</Box>
				</Box>

				<Box sx={CustomizationStyle.buttonsBlock}>
					<Button variant="outline" title="Cancel" onClick={handleCancelClick} />
					<Button title="Save changes" onClick={handleLogoSave} />
				</Box>
			</Box>

			<Box sx={CustomizationStyle.rightBoxWrapper}>
				<Typography sx={CustomizationStyle.rightHeading}>Preview</Typography>

				<Box sx={CustomizationStyle.sceletonWrapper}>
					<Box sx={CustomizationStyle.sceletonBox}>
						<Box sx={CustomizationStyle.logoPosition}>
							{Logo ? (
								<img
									src={getImagePreview(Logo)}
									alt="Logo preview"
									style={{width: 60, height: 'auto', objectFit: 'contain'}}
								/>
							) : null}
						</Box>
						<Stack spacing={1} alignItems="center" mb={4}>
							<Skeleton variant="rectangular" width="30%" height={12} sx={{marginBottom: '10px !important'}} />
							<Skeleton variant="rectangular" width="90%" height={10} />
							<Skeleton variant="rectangular" width="50%" height={10} />
						</Stack>

						<Stack direction="row" spacing={2}>
							<Stack spacing={1} width="50%">
								{Array.from({length: 17}).map((_, idx) => (
									<Skeleton key={idx} variant="rectangular" height={10} />
								))}
							</Stack>
							<Stack spacing={1} width="50%">
								{Array.from({length: 17}).map((_, idx) => (
									<Skeleton key={idx} variant="rectangular" height={10} />
								))}
							</Stack>
						</Stack>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default Customization;
